// DraftBubbleOverlay — 「作成中バブル」(custom-cursor-2026-09-19.md §2.5-4,
// time-permitting bonus): 相手がカーソルエディタを編集中 (`member.cursor-draft`)
// の間、その人が最後にストロークした位置に小さな吹き出しでライブプレビューを
// 出す。「キャンバス上でカーソルが生まれていく」演出。
//
// 設計上の責務分離 (architecture.md §16 / §19.6、NameLabelOverlay と同型):
//   - drawing engine: 一切触らない。pixel を描かない。
//   - frontend (このファイル): canvas と同じ relative wrapper の中に DOM で
//     `position: absolute` の吹き出しを散布するだけ。
//
// **最後の位置の記憶** はこのコンポーネントの責務ではない
// (`CanvasStage.tsx` が `chrome.nameLabels` の変化を見て uid ごとに永続
// (TTL 非依存) に保持し、`lastKnownPositionForUid` として渡す — NameLabel
// 自体は 1 秒 TTL で消えるが、エディタ操作中は描いていないことが多いため
// 別途長寿命な記憶が要る、§4 要検証事項)。

import type { CustomCursor } from '@oekaki/protocol';

import {
  COUNTER_ROTATE_TRANSFORM,
  canvasToLocalByZoomFlip,
} from '../../../infrastructure/drawing/viewport.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';
import { computeDraftBubblePosition, type LastKnownPosition } from './draftBubbleOverlay.js';

const DRAFT_BUBBLE_THUMB_SIZE_PX = 24;

export interface DraftBubbleOverlayProps {
  /** uid → 編集中のドラフト画素 (`SessionView.cursorDrafts`)。 */
  readonly drafts: Readonly<Record<string, CustomCursor>>;
  /** uid → 最後に観測されたストローク位置 (canvas native px)。無ければバブルを出さない。 */
  readonly lastKnownPositionForUid: (uid: string) => LastKnownPosition | null;
  readonly zoom: number;
  readonly flipX?: boolean;
  readonly canvasWidth?: number;
}

export const DraftBubbleOverlay = ({
  drafts,
  lastKnownPositionForUid,
  zoom,
  flipX,
  canvasWidth,
}: DraftBubbleOverlayProps): React.ReactNode => {
  const entries = Object.entries(drafts);
  if (entries.length === 0) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-0 h-full w-full"
      data-oekaki-draft-bubble-overlay
    >
      {entries.map(([uid, cursor]) => {
        const pos = lastKnownPositionForUid(uid);
        if (pos === null) return null;
        const { left, top } = computeDraftBubblePosition(pos, zoom, flipX, canvasWidth);
        // 表示回転: バブルは最後に観測したストローク位置を軸に逆回転して正立させる。
        const anchor = canvasToLocalByZoomFlip(pos.x, pos.y, zoom, flipX, canvasWidth);
        return (
          <div
            key={uid}
            data-oekaki-draft-bubble
            className="absolute flex flex-col items-center gap-0.5 rounded-md border border-black/40 bg-white/90 p-1 shadow"
            style={{
              left: `${left}px`,
              top: `${top}px`,
              transform: COUNTER_ROTATE_TRANSFORM,
              transformOrigin: `${anchor.x - left}px ${anchor.y - top}px`,
            }}
          >
            <CustomCursorCanvas
              pixels={decodeCursorPixels(cursor)}
              sizePx={DRAFT_BUBBLE_THUMB_SIZE_PX}
              checker
              alwaysRender
            />
            <span className="animate-pulse text-[8px] leading-none text-black">…</span>
          </div>
        );
      })}
    </div>
  );
};
